import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedSnackbar,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import { IGetListRequest, IResponse, IServerError } from "../../../../models/common";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	getAddOnsScreenParentName,
	scrollPanelFieldsToTop,
	sortDDLByLabel,
	useNavigateToLogout,
} from "../../../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AddOnsCombineSetupService } from "../../../../services/AddOns/Accounting/AddOnsCombineSetupService";
import { AddOnsCombineSetupValidationSchema } from "./validator";
import { AgendrixSetupFieldsPanel } from "./Panels/AgendrixSetupFieldsPanel";
import { BasicSetupFormToolbar } from "../../../BasicSetupFormToolbar";
import { DashboardServices } from "../../../../services/common/DashboardServices";
import { EasyWorkForceSetupFieldsPanel } from "./Panels/EasyWorkForceSetupFieldsPanel";
import { HrWizeSetupFieldsPanel } from "./Panels/HrWizeSetupFieldsPanel";
import { IAddOnsCombineSetup } from "../../../../models/AddOns/Accounting/AddOnsCombineSetup";
import { IAppState } from "../../../../store/reducers";
import {
	INITIAL_SNACKBAR_DATA,
} from "../../../../common/constants";
import { NetSuiteSetupFieldsLeftPanel } from "./Panels/NetSuiteSetupFieldsLeftPanel";
import { NetSuiteSetupFieldsRightPanel } from "./Panels/NetSuiteSetupFieldsRightPanel";
import { NoFoundPage } from "../../../../pages/Anonymous/NoFoundPage";
import { QuickBooksTimeSetupFieldsPanel } from "./Panels/QuickBooksTimeSetupFieldsPanel";
import { SevenShiftsSetupFieldsPanel } from "./Panels/SevenShiftsSetupFieldsPanel";
import { SwipeClockSetupFieldsPanel } from "./Panels/SwipeClockSetupFieldsPanel";
import { TogglSetupFieldsPanel } from "./Panels/TogglSetupFieldsPanel";
import { TwoColumnLayout } from "./TwoColumnLayout";
import { WhenIWorkSetupFieldsPanel } from "./Panels/WhenIWorkSetupFieldsPanel";
import { WorkFlowMaxSetupFieldsPanel } from "./Panels/WorkFlowMaxSetupFieldsPanel";
import { WorkableSetupFieldsPanel } from "./Panels/WorkableSetupFieldsPanel";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../../store/actions/stickyNavBarActions";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IAddOnsCombineSetupPage { }
const SINGLE_RECORD_DATA_QUERY_KEY = "single-data-record";

const useGetData = (
	AddOnsCombineSetupPageName: string,
	clientId: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, AddOnsCombineSetupPageName, clientId, navigateToLogout],
		queryFn: (data) => {
			return AddOnsCombineSetupService.getAddOnsCombineSetupById(AddOnsCombineSetupPageName, clientId, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const getAccountSettingsDDL = (AddOnsCombineSetupPageName: string, clientId: string,) =>
	useQuery({
		queryKey: ["account-settings-ddl", AddOnsCombineSetupPageName, clientId],
		queryFn: (data) => {
			return AddOnsCombineSetupService.getAccountSettingsDDL(AddOnsCombineSetupPageName, clientId);
		},
		refetchOnWindowFocus: false,
	});

const DASHBOARD_API_DATA_QUERY_KEY = "dashboard-api"
const useGetDashboardMenuData = (
	clientId: string,
	taxYear: number
) =>
	useQuery({
		queryKey: [DASHBOARD_API_DATA_QUERY_KEY, clientId, taxYear],
		queryFn: (data) => {
			return DashboardServices.getDashboardMenuData(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});
export const AddOnsCombineSetup: React.FC<IAddOnsCombineSetupPage> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [addOnsCombineSetupId, setAddOnsCombineSetupId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();

	// Fucking combine component props defines here
	const { id, AddOnsCombineSetupPageName } = useParams();
	const [editRecordId, setEditRecordId] = useState(id);
	// addOnsCombineSetuPageName would be like 
	// net_suite
	// zoho_books
	// xero
	// quick_books
	// kashoo


	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(AddOnsCombineSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",

	});

	const { isPending: isPendingDashboardAPIData, data: dashboardApiData } =
		useGetDashboardMenuData(clientId, taxYear);

	const [addOnsCombineSetuPageName, setAddOnsCombineSetuPageName] = useState<string>("");
	useEffect(() => {
		if (AddOnsCombineSetupPageName && AddOnsCombineSetupPageName.length > 0) {

			setAddOnsCombineSetuPageName(AddOnsCombineSetupPageName);
			if (AddOnsCombineSetupPageName !== addOnsCombineSetuPageName) {
				formMethods.reset();
			}
		}
	}, [addOnsCombineSetuPageName, AddOnsCombineSetupPageName]);

	const { isPending: isPendingAccountSettings, data: accountSettingsDDL } =
		getAccountSettingsDDL(addOnsCombineSetuPageName, clientId);

	const formatLabel = (key: string) => {
		return key
			.replace(/([a-z])([A-Z])/g, "$1 $2")
			.replace(/accounts?$/i, " Accounts")
			.replace(/[_-]+/g, " ")
			.replace(/\s+/g, " ")
			.trim()
			.toLowerCase()
			.replace(/\b\w/g, (c) => c.toUpperCase());
	}
	const extractGroups = (source: Record<string, any>) => {
		const groups: any = [];

		Object.entries(source).forEach(([key, value]) => {
			if (Array.isArray(value)) {
				groups.push({
					label:
						key === "DIRECTCOSTS" ? "DIRECT COSTS" :
							key === "CURRLIAB" ? "CURRENT LIABILITY" :
								key === "TERMLIAB" ? "TERM LIABILITY" :
									formatLabel(key),
					slug: key,
					value: key,
					options: value,
				});
			} else if (value && typeof value === "object") {
				Object.entries(value).forEach(([nestedKey, nestedValue]) => {
					if (Array.isArray(nestedValue)) {
						groups.push({
							label: formatLabel(nestedKey),
							slug: nestedKey,
							value: nestedKey,
							options: nestedValue,
						});
					}
				});
			}
		});

		return groups;
	}

	const updateAccountSettingsDDLWithParentChildDDL = useMemo(() => {
		if (accountSettingsDDL && accountSettingsDDL.allaccounts) return extractGroups(accountSettingsDDL.allaccounts);
	}, [accountSettingsDDL]);

	// useEffect(() => {
	// 	if (accountSettingsDDL && accountSettingsDDL.status === 400 && addOnsCombineSetuPageName === 'zoho_books') {
	// 		navigate(`/add-ons/zoho-books/connect-zoho`)
	// 	} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && addOnsCombineSetuPageName === 'net_suite') {
	// 		navigate(`/add-ons/net-suite/setup`)
	// 	}
	// }, [accountSettingsDDL])

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		addOnsCombineSetuPageName,
		clientId,
		// editRecordId,
		navigateToLogout
	);

	const { isPending: isSubmitting, mutate: submitAddOnsCombineSetupFormFields } =
		useMutation({
			mutationFn: AddOnsCombineSetupService.create,
			onSuccess: (res: IAddOnsCombineSetup | IServerError | IResponse | any) => {

				if (res.status === 200) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
					let path = `/add-ons/setup/${addOnsCombineSetuPageName}`;
					navigate(path);
				} else if (res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
					let path = `/add-ons/setup/${addOnsCombineSetuPageName}`;
					navigate(path);
				} else {
					setSnackbarData({
						open: true,
						message: res.data.message
							? res.data.message : res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});


	const onSubmit = (values: any) => {
		const payload = {
			clientId: clientId,
			navigateToLogout,
			addOnsCombineSetuPageName: addOnsCombineSetuPageName,
			// organizationId : organizationId 
			// id: id && id,
			...(addOnsCombineSetuPageName === "net_suite" && {
				authenticationMode: values.authenticationMode,
				email: values.email,
				password: values.password,
				accountNumber: values.accountNumber,
				accountType: values.accountType
			}),
			...(addOnsCombineSetuPageName === "toggl" && {
				email: values.email,
				password: values.password
			}),
			...(addOnsCombineSetuPageName === "agendrix" && {
				fuck: "fuck" // screen pending
			}),
			...(addOnsCombineSetuPageName === "when_i_work" && {
				email: values.email,
				password: values.password
			}),
			...(addOnsCombineSetuPageName === "swipe_clock" && {
				accountant_id: values.accountant_id,
				site_id: values.site_id,
				api_secret: values.api_secret
			}),
			...(addOnsCombineSetuPageName === "easy_work_force" && {
				company_code: values.company_code,
				username: values.username,
				password: values.password
			}),
			...(addOnsCombineSetuPageName === "quick_books_time" && {
				access_token: values.access_token
			}),
			...(addOnsCombineSetuPageName === "work_flow_max" && {
				account_key: values.account_key,
				api_key: values.api_key
			}),
			...(addOnsCombineSetuPageName === "seven_shifts" && {
				api_key: values.api_key
			}),
			...(addOnsCombineSetuPageName === "hr_wize" && {
				api_key: values.api_key
			}),
			...(addOnsCombineSetuPageName === "workable" && {
				fuck: "fuck" // screen pending
			}),
		};


		submitAddOnsCombineSetupFormFields(payload);
	};

	// useEffect(() => {
	// 	if (clientId && selectedDataRecord) {
	// 		Object.keys(selectedDataRecord).forEach((fieldName) => {
	// 			const fieldValue = selectedDataRecord[fieldName];
	// 			formMethods.setValue(fieldName, fieldValue ?? undefined);
	// 			// if (fieldName === "clientId") {
	// 			// 	// let path = `/add-ons/setup${addOnsCombineSetuPageName}/${clientId}`
	// 			// 	// navigate(path)
	// 			// }
	// 			if (fieldValue && typeof (fieldValue) === 'object') {
	// 				Object.keys(fieldValue).forEach((objFieldName) => {
	// 					const objFieldValue = fieldValue[objFieldName];
	// 					formMethods.setValue(objFieldName, objFieldValue ?? undefined);
	// 				});
	// 			}
	// 		});
	// 	}
	// }, [clientId, selectedDataRecord]);

	const { isPending: isDeleting, mutate: deleteAddOnsCombineSetupById } = useMutation({
		mutationFn: AddOnsCombineSetupService.delAddOnsCombineSetup,
		onSuccess: (res: string | IServerError | IResponse | any) => {
			if (typeof res === "string") {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message ? res.message : "Something went wrong.",
					severity: res.status === false ? "error" : res.success === false ? "error" : "success",
				});
			}
			formMethods.reset();
			scrollPanelFieldsToTop();
			let path = `/add-ons/setup/${addOnsCombineSetuPageName}`;
			navigate(path);
		},
		onError: (err) => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});


	const deleteAddOnsCombineSetupField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				addOnsCombineSetuPageName: addOnsCombineSetuPageName,
				clientId: clientId,
				navigateToLogout: navigateToLogout,
			}
			deleteAddOnsCombineSetupById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setAddOnsCombineSetupId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};

	const expandedLabels = ["Add-Ons", `${getAddOnsScreenParentName(addOnsCombineSetuPageName)}`];
	const expandedAddOnsOrListItem = "Setup";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === `${getAddOnsScreenParentName(addOnsCombineSetuPageName)}` && item.label === expandedAddOnsOrListItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === `${getAddOnsScreenParentName(addOnsCombineSetuPageName)}` && parentLabel === `${getAddOnsScreenParentName(addOnsCombineSetuPageName)}` && item.label === expandedAddOnsOrListItem && `/add-ons/setup/${AddOnsCombineSetupPageName}`
			};
			return enhancedItem;
		});
	};


	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);


	const onClickHereClickedHandler = async () => {
		try {
			const response = await AddOnsCombineSetupService.getLoginAccount(addOnsCombineSetuPageName, clientId);
			if (typeof (response) === "string") {
				// window.location.href = response;
				window.open(response, "_blank");
			}
		} catch { }
	}

	let acceptableAddOnsCombineSetupPages = ["net_suite", "toggl", "agendrix", "when_i_work", "swipe_clock", "easy_work_force", "quick_books_time", "work_flow_max", "seven_shifts", "hr_wize", "workable"]

	return (
		<>{AddOnsCombineSetupPageName && acceptableAddOnsCombineSetupPages.includes(AddOnsCombineSetupPageName) ? <Grid>
			<BackdropCircularProgress
				open={
					isSubmitting ||
					isPendingById ||
					isDeleting || isPendingAccountSettings ||
					isPendingDashboardAPIData
				}
				testId="addOns-combine-setup"
			/>
			{/* {accountSettingsDDL && accountSettingsDDL.status === 400 && addOnsCombineSetuPageName === 'xero' ?
				<>
					<div
						style={{
							margin: "15px 0",
							fontSize: "1.5rem",
							fontWeight: "bold",
							fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
						}}
					>
						{"Setup"}
					</div>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }} sx={{ background: "#ffffff", padding: "2rem", alignItems: "center", textAlign: "center", fontSize: ".875rem", border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "2px" }}>
							<Link
								to={`#`}
								onClick={onClickHereClickedHandler}
								className="no-underline"
								style={{ color: theme.palette.primary.light }}
							>
								Click here
							</Link>
							&nbsp;&nbsp;
							<span style={{ color: "#FF6600", fontSize: ".875rem" }}>
								to allow SimplePay to access your Organization(s).
							</span>
						</Grid>
					</Grid></>
				: accountSettingsDDL && accountSettingsDDL.status === 400 && addOnsCombineSetuPageName === 'quick_books' ?
					<>
						<div
							style={{
								margin: "15px 0",
								fontSize: "1.5rem",
								fontWeight: "bold",
								fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
							}}
						>
							{"Setup"}
						</div>
						<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }} sx={{ background: "#ffffff" }}>
								<StandardPanel
									title="Integration Type"
									testId={`special-earnings-info`}
									variant="outlined"
									borderBottom={false}
									borderHide={true}
								>
									<>Coming Soon..</>
								</StandardPanel>
							</Grid>
						</Grid></>
					: accountSettingsDDL && accountSettingsDDL.status === 400 && addOnsCombineSetuPageName === 'kashoo' ?
						<>
							<div
								style={{
									margin: "15px 0",
									fontSize: "1.5rem",
									fontWeight: "bold",
									fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
								}}
							>
								{"Setup"}
							</div>
							<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }} sx={{ background: "#ffffff", padding: "2rem", alignItems: "center", textAlign: "center", fontSize: ".875rem", border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "2px" }}>
									<Link
										to={`#`}
										// onClick={onClickHereClickedHandler}
										className="no-underline"
										style={{ color: theme.palette.primary.light }}
									>
										Connect With Kashoo
									</Link>
								</Grid>
							</Grid></>
						: accountSettingsDDL && accountSettingsDDL.expenseaccounts ? */}
			<Grid>

				<DeleteConfirmationDialog
					open={openDialog}
					deleteActionHandler={deleteAddOnsCombineSetupField}
					cancelActionHandler={onCloseHandler}
					testId="addOns-combine-setup-confirmation-dialog"
				/>
				{addOnsCombineSetuPageName === 'workable' ? (
					<>
						{/* <Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
											<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }} sx={{ background: "#ffffff", padding: "2rem", mt: 2, alignItems: "center", textAlign: "center", fontSize: ".875rem", border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: "2px" }}>
												<Link
													to={`#`}
													className="no-underline"
													style={{ color: theme.palette.primary.light }}
												>
													Click here
												</Link>
												&nbsp;&nbsp;
												<span style={{ color: "#FF6600", fontSize: ".875rem" }}>
													to allow SimplePay to access your Organization(s).
												</span>
											</Grid>
										</Grid> */}
						<div
							style={{
								margin: "15px 0",
								fontSize: "1.5rem",
								fontWeight: "bold",
								fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
							}}
						>
							{"Connect Workable"}
						</div>
						<WorkableSetupFieldsPanel />
					</>
				) : (
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<FormProvider {...formMethods}>
								<form onSubmit={formMethods.handleSubmit(onSubmit)}>
									<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
										<Grid
											size={{ xs: 12, sm: 12, md: 8.5, lg: 8, xl: 8 }}
											sx={{
												// border: "1px solid rgba(0, 0, 0, 0.12)",
												// borderRadius: "3px",
												// marginTop: "16px",
												background: "#ffffff",
											}}
										>
											<TwoColumnLayout
												leftColumnContent={
													<>
														{addOnsCombineSetuPageName === 'net_suite' ? <StandardPanel
															title="Login Details"
															testId={`Login-Details`}
															variant="outlined"
															borderBottom={false}
															borderHide={true}
														>
															<NetSuiteSetupFieldsLeftPanel />
														</StandardPanel> :
															addOnsCombineSetuPageName === 'toggl' ? <>
																<StandardPanel
																	title="API Details"
																	testId={`Api-Details`}
																	variant="outlined"
																	borderBottom={false}
																	borderHide={true}
																>
																	<TogglSetupFieldsPanel />
																</StandardPanel>
															</> :

																addOnsCombineSetuPageName === 'agendrix' ? <>
																	<StandardPanel
																		title=""
																		testId={``}
																		variant="outlined"
																		borderBottom={false}
																		borderHide={true}
																	>
																		<AgendrixSetupFieldsPanel />
																	</StandardPanel>
																</> :

																	addOnsCombineSetuPageName === 'when_i_work' ? <>
																		<StandardPanel
																			title="API Details"
																			testId={`Api-Details`}
																			variant="outlined"
																			borderBottom={false}
																			borderHide={true}
																		>
																			<WhenIWorkSetupFieldsPanel />
																		</StandardPanel>
																	</> :


																		addOnsCombineSetuPageName === 'swipe_clock' ? <>
																			<StandardPanel
																				title="API Details"
																				testId={`Api-Details`}
																				variant="outlined"
																				borderBottom={false}
																				borderHide={true}
																			>
																				<SwipeClockSetupFieldsPanel />
																			</StandardPanel>
																		</> :



																			addOnsCombineSetuPageName === 'easy_work_force' ? <>
																				<StandardPanel
																					title="API Details"
																					testId={`Api-Details`}
																					variant="outlined"
																					borderBottom={false}
																					borderHide={true}
																				>
																					<EasyWorkForceSetupFieldsPanel />
																				</StandardPanel>
																			</> :



																				addOnsCombineSetuPageName === 'quick_books_time' ? <>
																					<StandardPanel
																						title="API Details"
																						testId={`Api-Details`}
																						variant="outlined"
																						borderBottom={false}
																						borderHide={true}
																					>
																						<QuickBooksTimeSetupFieldsPanel />
																					</StandardPanel>
																				</> :
																					addOnsCombineSetuPageName === 'work_flow_max' ? <>
																						<StandardPanel
																							title="API Details"
																							testId={`Api-Details`}
																							variant="outlined"
																							borderBottom={false}
																							borderHide={true}
																						>
																							<WorkFlowMaxSetupFieldsPanel />
																						</StandardPanel>
																					</> :
																						addOnsCombineSetuPageName === 'seven_shifts' ? <>
																							<StandardPanel
																								title="API Details"
																								testId={`Api-Details`}
																								variant="outlined"
																								borderBottom={false}
																								borderHide={true}
																							>
																								<SevenShiftsSetupFieldsPanel />
																							</StandardPanel>
																						</> :
																							addOnsCombineSetuPageName === 'hr_wize' ? <>
																								<StandardPanel
																									title="API Details"
																									testId={`Api-Details`}
																									variant="outlined"
																									borderBottom={false}
																									borderHide={true}
																								>
																									<HrWizeSetupFieldsPanel />
																								</StandardPanel>
																							</> :
																								<></>
														}
													</>
												}
												rightColumnContent={
													<>
														{addOnsCombineSetuPageName === 'net_suite' ? <StandardPanel
															title="Single Sign-on"
															testId={`Single-Sign-on`}
															variant="outlined"
															borderBottom={false}
															borderHide={true}
														>
															<NetSuiteSetupFieldsRightPanel />
														</StandardPanel> : <></>
														}
													</>
												}
												toolbar={
													<BasicSetupFormToolbar
														onDeleteClicked={
															addOnsCombineSetuPageName === 'net_suite' ? onDeleteClicked :
																addOnsCombineSetuPageName === 'when_i_work' ? onDeleteClicked :
																	undefined
														}
														isSubmitting={isSubmitting}
														id={clientId}
														pageTitle={"Setup"}
													/>
												}
												testId="form-layout"
												showBorder={true}
											/>
										</Grid>
										<Grid size={{ xs: 12, sm: 12, md: 0.2, lg: 1, xl: 1 }}></Grid>
									</Grid>
								</form>
							</FormProvider>

						</Grid>
					</Grid>)}
				<EnhancedSnackbar
					message={snackbarData.message}
					onCloseHandler={onSnackbarClosed}
					severity={snackbarData.severity}
					testId={"addOns-combine-setup-snackbar"}
					open={snackbarData.open}
				/>
			</Grid>
			{/* : <></>} */}
		</Grid> : <NoFoundPage />}</>
	);
};
